// src/components/store/PaginatedProductSection.tsx
"use client";

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import {
  resolveProductCardComponent,
  PRODUCT_CARD_COMPONENTS,
} from "./sections/product-cards";

/**
 * যেকোনো ইমেজ লিংককে বর্তমান ডোমেইনের (যেমন: https://bestbdbazar.com/media/...)
 * সিকিউর ও সঠিক URL-এ রূপান্তর করার হেল্পার ফাংশন
 */
export function resolveStoreMediaUrl(rawUrl: any, subdomain?: string): any {
  if (!rawUrl || typeof rawUrl !== "string") return rawUrl;
  const trimmed = rawUrl.trim();
  if (!trimmed || trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
    return trimmed;
  }

  let relativePath = trimmed;

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      if (
        parsed.pathname.startsWith("/media/") ||
        parsed.pathname.startsWith("/static/") ||
        parsed.hostname.includes("localhost") ||
        parsed.hostname === "127.0.0.1" ||
        parsed.hostname.includes("xylotechsolution.com") ||
        parsed.hostname.includes("bestbdbazar.com")
      ) {
        relativePath = parsed.pathname + parsed.search;
      } else {
        return trimmed.replace(/^http:\/\//i, "https://");
      }
    } catch {
      return trimmed.replace(/^http:\/\//i, "https://");
    }
  } else if (trimmed.startsWith("media/")) {
    relativePath = `/${trimmed}`;
  }

  if (typeof window !== "undefined") {
    const { hostname, origin } = window.location;
    const isLocal = hostname.includes("localhost") || hostname === "127.0.0.1";

    if (isLocal) {
      const activeSub =
        subdomain && subdomain !== "www"
          ? subdomain
          : hostname.split(".")[0] || "nextlook";
      return `http://${activeSub}.localhost:8000${
        relativePath.startsWith("/") ? "" : "/"
      }${relativePath}`;
    }

    // কাস্টম ডোমেইন (https://bestbdbazar.com) বা লাইভ সাবডোমেইনের নিজস্ব Origin ব্যবহার করা
    return `${origin}${relativePath.startsWith("/") ? "" : "/"}${relativePath}`;
  }

  return relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
}

/**
 * প্রোডাক্ট অবজেক্টের ভেতরের সব ইমেজ ফিল্ড (image, thumbnail, images array) ফিক্স করা
 */
export function normalizeProductList(products: any[], subdomain?: string): any[] {
  if (!Array.isArray(products)) return [];

  return products.map((product) => {
    if (!product || typeof product !== "object") return product;

    const rawPrimaryImage =
      product.image ||
      product.image_url ||
      product.imageUrl ||
      product.thumbnail ||
      product.featured_image ||
      product.cover_image ||
      (Array.isArray(product.images) && product.images.length > 0
        ? typeof product.images[0] === "string"
          ? product.images[0]
          : product.images[0]?.image ||
            product.images[0]?.image_url ||
            product.images[0]?.url
        : null);

    const fixedPrimary = resolveStoreMediaUrl(rawPrimaryImage, subdomain);

    const fixedImages = Array.isArray(product.images)
      ? product.images.map((item: any) => {
          if (typeof item === "string") {
            return resolveStoreMediaUrl(item, subdomain);
          }
          if (item && typeof item === "object") {
            return {
              ...item,
              image: resolveStoreMediaUrl(item.image || item.url || item.image_url, subdomain),
              image_url: resolveStoreMediaUrl(item.image_url || item.image || item.url, subdomain),
              url: resolveStoreMediaUrl(item.url || item.image || item.image_url, subdomain),
            };
          }
          return item;
        })
      : product.images;

    return {
      ...product,
      image: fixedPrimary || product.image,
      image_url: fixedPrimary || product.image_url,
      imageUrl: fixedPrimary || product.imageUrl,
      thumbnail: resolveStoreMediaUrl(product.thumbnail || rawPrimaryImage, subdomain),
      featured_image: resolveStoreMediaUrl(product.featured_image || rawPrimaryImage, subdomain),
      images: fixedImages,
    };
  });
}

interface PaginatedProductSectionProps {
  badgeText?: string;
  title: string;
  count?: number;
  products: any[];
  itemsPerPage?: number;
  viewAllLink?: string;
  variant?: string;
  primaryColor?: string;
  subdomain?: string;
}

export default function PaginatedProductSection({
  badgeText = "Products",
  title = "Featured Products",
  count,
  products = [],
  itemsPerPage = 8,
  viewAllLink = "/products",
  variant = "motion_3d",
  primaryColor = "#059669",
  subdomain = "nextlook",
}: PaginatedProductSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeVariant, setActiveVariant] = useState<string>(variant || "motion_3d");
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const syncDesign = () => {
      if (variant) {
        setActiveVariant(variant);
      }
      if (typeof window !== "undefined") {
        try {
          const rawSectionDesigns = localStorage.getItem("section_designs");
          if (rawSectionDesigns) {
            const parsed = JSON.parse(rawSectionDesigns);
            if (parsed.featured_products) {
              setActiveVariant(parsed.featured_products);
            }
          }
        } catch {}
      }
    };

    syncDesign();
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncDesign);
      window.addEventListener("theme_settings_updated", syncDesign);
      return () => {
        window.removeEventListener("storage", syncDesign);
        window.removeEventListener("theme_settings_updated", syncDesign);
      };
    }
  }, [variant]);

  // সব প্রোডাক্টের ইমেজ URL বর্তমান ডোমেইন অনুযায়ী ফিক্স করা
  const normalizedProducts = useMemo(
    () => normalizeProductList(products, subdomain),
    [products, subdomain, isClient]
  );

  const totalItems = normalizedProducts?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = normalizedProducts.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (!normalizedProducts || normalizedProducts.length === 0) return null;

  const CardComponent = resolveProductCardComponent(activeVariant);

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
        <div>
          <span
            style={{ color: primaryColor }}
            className="text-[11px] sm:text-xs font-black uppercase tracking-wider flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" /> {badgeText}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <h2 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight">
              {title}
            </h2>
            <span className="text-xs sm:text-sm font-bold text-slate-400">
              ({count ?? totalItems})
            </span>
          </div>
        </div>

        {viewAllLink && (
          <Link
            href={viewAllLink}
            style={{ backgroundColor: primaryColor }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer hover:opacity-90"
          >
            <span>View All</span>
            <ArrowRight className="w-3.5 h-3.5 shrink-0" />
          </Link>
        )}
      </div>

      {/* Dynamic Product Card Grid (Supports All 105 Variants) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-6 items-stretch">
        {currentProducts.map((product: any, idx: number) => (
          <div key={product.id || idx} className="h-full flex">
            <CardComponent
              product={product}
              index={startIndex + idx}
              subdomain={subdomain}
              primaryColor={primaryColor}
            />
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 sm:gap-2 pt-6">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              type="button"
              onClick={() => setCurrentPage(page)}
              style={
                currentPage === page
                  ? { backgroundColor: primaryColor, borderColor: primaryColor }
                  : undefined
              }
              className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                currentPage === page
                  ? "text-white shadow-xs"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}

export { PRODUCT_CARD_COMPONENTS };